import { ReactNode, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Button, Spinner } from "reactstrap"

import { IProject, IProjectFollowership } from "@api/schema"
import FollowershipCard from "@components/entityTypes/projectFollowership/FollowershipCard"
import { withDynamicNamespaces } from "@components/hoc/withDynamicNamespaces"
import OnboardingModal from "@components/usecases/onboarding/OnboardingModal"
import { addNotificationAction } from "@redux/actions/notifications"
import { IFormikActions, createModelAction, deleteModelAction, usecaseKeyForDeleteModel } from "@redux/helper/actions"
import { AppState } from "@redux/reducer"
import { selectSingleEntityUsecaseState } from "@redux/reducer/data"
import { selectMyFollowerships } from "@redux/reducer/myProjects"
import { ActionTypes, EntityType } from "@redux/reduxTypes"
import { OnboardingType, addNewProjectFollowershipOnboardingAction, selectOtherExistingOnboardingType } from "@redux/usecases/onboarding"
import { useCurrentUser } from "@services/hooks/useCurrentUser"
import { useToggleState } from "@services/hooks/useToggleState"
import { NamespaceShortcut, prefixedKey, useDynamicTranslation } from "@services/i18n"
import { useTooltip } from "@services/tooltipHook"
import { idFromIModelOrIRI, iriFromEntityTypeAndId, logUnhandledUnexpectedError } from "@services/util"

import Icon, { IconName } from "../../common/Icon"

const usedNamespaces: NamespaceShortcut[] = ["onboarding", "follow-project"]

interface IProps {
  project: IProject
  showFollowershipCount: boolean
  /**
   * Allow the user to remove her followership by clicking on the FollowButton.
   * If set to false, the OnboardingModal is shown instead.
   * Default is true.
   */
  allowWithdrawalOfOnboardingFollowshipInsteadOfModal?: boolean
}

/**
 * This component provides a "follow the project" button.
 */
const FollowButton: React.FC<IProps> = (props: IProps) => {
  const {
    project,
    showFollowershipCount,
    allowWithdrawalOfOnboardingFollowshipInsteadOfModal = true
  } = props
  const t = useDynamicTranslation()
  const dispatch = useDispatch()

  const { isTrue: modal, toggle: toggleModal, setIsTrue: setModal } = useToggleState(false)

  const { currentUser, isAuthenticated, } = useCurrentUser()

  const createdOnboardingProjectFollowership = useSelector((state: AppState) => state.onboardingData.newProjectFollowership)
  const myFollowershipProjectIds = useSelector((state: AppState) => selectMyFollowerships(state)?.map(ship => idFromIModelOrIRI(ship.project)))
  const currentFollowership = currentUser?.followerships?.find(p => idFromIModelOrIRI(p.project) === idFromIModelOrIRI(project))
  const creationState = useSelector((state: AppState) => selectSingleEntityUsecaseState(state, EntityType.ProjectFollowership, ActionTypes.Create))

  const thisOnboardingType = OnboardingType.NewProjectFollowership
  const otherExistingOnboardingType = useSelector((state: AppState) => selectOtherExistingOnboardingType(state, thisOnboardingType))
  const currentProjectAlreadyUsedForOnboardingFollowership = createdOnboardingProjectFollowership && project && idFromIModelOrIRI(createdOnboardingProjectFollowership.project) === idFromIModelOrIRI(project)

  const loading = useSelector((state: AppState) =>
    selectSingleEntityUsecaseState(state, EntityType.ProjectFollowership, ActionTypes.Create)?.isLoading
    || selectSingleEntityUsecaseState(state, EntityType.ProjectFollowership, usecaseKeyForDeleteModel(currentFollowership))?.isLoading
  )

  useEffect(() => {
    if (!createdOnboardingProjectFollowership) {
      setModal(false)
    }
  }, [createdOnboardingProjectFollowership])

  useEffect(() => {
    if (creationState?.loadingError) {
      dispatch(addNotificationAction("error:validate.general.submissionFailed", "error"))
      logUnhandledUnexpectedError(creationState.loadingError, 'server')
    }
  }, [creationState])

  const createFollowership = (e: React.MouseEvent<HTMLElement>) => {
    // keep this mouse event for us; no parent container should handle it
    e.preventDefault()
    e.stopPropagation()

    const actions = {} as IFormikActions
    const newFollowership = {
      project,
    } as IProjectFollowership

    if (isAuthenticated) {
      newFollowership.user = iriFromEntityTypeAndId(EntityType.User, currentUser.id)
      // NOTE currentUser gets automatically refreshed thanks to `refreshConnectedEntities` in `generalSaga`
      dispatch(createModelAction(EntityType.ProjectFollowership, newFollowership, actions))
    } else {
      // NOTE newFollowership.user is set in onboarding-sagas
      // if not authenticated, the followership is stored in the appstate
      dispatch(addNewProjectFollowershipOnboardingAction(newFollowership))
    }
  }

  const deleteFollowership = (e: React.MouseEvent<HTMLElement>) => {
    // keep this mouse event for us; no parent container should handle it
    e.preventDefault()
    e.stopPropagation()

    if (isAuthenticated) {
      if (currentFollowership) {
        dispatch(deleteModelAction(EntityType.ProjectFollowership, currentFollowership, null))
      }
    } else {
      // if not authenticated, the followership is stored in the appstate - we reset it to null
      dispatch(addNewProjectFollowershipOnboardingAction(null))
    }
  }


  // the possible states of the button
  enum ButtonStates {
    NotLoggedIn = "notLoggedIn",
    NotLoggedInButPendingFollowershipOfCurrentProject = "notLoggedInButPendingFollowershipOfCurrentProject",
    NotLoggedInButPendingFollowershipOfOtherProject = "notLoggedInButPendingFollowershipOfOtherProject",
    NotLoggedInButOtherOnboardingData = "notLoggedInButOtherOnboardingData",
    LoggedIn = "loggedIn",
    LoggedInButAlreadyFollowing = "loggedInButAlreadyFollower",
  }

  // type of a button option
  interface FollowButtonOption {
    title: string
    icon: IconName
    active: boolean
    onClick: (args: any) => void
  }

  // type of the list of follow button options
  type FollowershipButtons = {
    [key in ButtonStates]: FollowButtonOption
  }

  // list of possible options of the button
  const buttonOptions: FollowershipButtons = {
    [ButtonStates.NotLoggedIn]: {
      title: t("follow-project", "followProject"),
      icon: "thumbs-up",
      active: false,
      onClick: (e: React.MouseEvent<HTMLElement>) => {
        createFollowership(e)
        toggleModal()
      },
    },
    [ButtonStates.NotLoggedInButPendingFollowershipOfCurrentProject]: {
      title: allowWithdrawalOfOnboardingFollowshipInsteadOfModal
        ? t("follow-project", "unfollowProject")
        : t("onboarding", "accountRequired"),
      icon: "thumbs-up",
      active: true,
      onClick: allowWithdrawalOfOnboardingFollowshipInsteadOfModal
        ? deleteFollowership
        : toggleModal
    },
    [ButtonStates.NotLoggedInButPendingFollowershipOfOtherProject]: {
      title: t("follow-project", "followProject"),
      icon: "thumbs-up",
      active: false,
      onClick: toggleModal,
    },
    [ButtonStates.NotLoggedInButOtherOnboardingData]: {
      title: t("onboarding", "notAllowedBecauseOtherOnboardingDataExists"),
      icon: "thumbs-up",
      active: false,
      onClick: toggleModal,
    },
    [ButtonStates.LoggedIn]: {
      title: t("follow-project", "followProject"),
      icon: "thumbs-up",
      active: false,
      onClick: createFollowership,
    },
    [ButtonStates.LoggedInButAlreadyFollowing]: {
      title: t("follow-project", "unfollowProject"),
      icon: "thumbs-up",
      active: true,
      onClick: deleteFollowership,
    },
  }

  // calculate the real used button option
  let buttonOption = buttonOptions[ButtonStates.NotLoggedIn]
  if (isAuthenticated) {
    // logged in

    // project is part of his followerships
    if (myFollowershipProjectIds?.includes(project.id)) {
      buttonOption = buttonOptions[ButtonStates.LoggedInButAlreadyFollowing]
    } else {
      buttonOption = buttonOptions[ButtonStates.LoggedIn]
    }
  } else {
    // not logged in
    if (createdOnboardingProjectFollowership) {
      if (currentProjectAlreadyUsedForOnboardingFollowership) {
        buttonOption = buttonOptions[ButtonStates.NotLoggedInButPendingFollowershipOfCurrentProject]
      } else {
        buttonOption = buttonOptions[ButtonStates.NotLoggedInButPendingFollowershipOfOtherProject]
      }
    } else {
      if (otherExistingOnboardingType) {
        buttonOption = buttonOptions[ButtonStates.NotLoggedInButOtherOnboardingData]
      } else {
        buttonOption = buttonOptions[ButtonStates.NotLoggedIn]
      }
    }
  }

  const { tooltipTargetRef, ToolTip } = useTooltip(buttonOption.title)

  return project && <>
    <Button
      className={"round-icon-btn" + (buttonOption.active ? " round-icon-btn--active" : "")}
      title={buttonOption.title}
      innerRef={tooltipTargetRef}
      onClick={buttonOption.onClick}
    >
      {loading
        ? <Spinner />
        : showFollowershipCount && (project.followerCount > 0) && <div
          className={"round-icon-btn--count" + (buttonOption.active ? " round-icon-btn--count--active" : "")}
        >
          {project.followerCount}
        </div>
      }
      <Icon name={buttonOption.icon} size={25} title={buttonOption.title} />
    </Button>
    {ToolTip}

    <OnboardingModal
      // modal specific props first
      modal={modal}
      toggleModal={toggleModal}

      // onboarding component's props from here

      thisOnboardingType={thisOnboardingType}

      creationSuccessUI={(projectFollowership: IProjectFollowership): ReactNode =>
        <FollowershipCard
          projectFollowership={projectFollowership}
        />
      }

      // NOTE we do not pass any "createdAuthenticatedObject/authenticatedCreation" params,
      // because the logged-in user gets immediate feedback on the page

      currentOriginIsOriginForOnboardingObject={currentProjectAlreadyUsedForOnboardingFollowership}

      // NOTE "anotherProjectUsedInOnboarding" key exists also in "common:page.projects.apply"
      onboardingObjectIsFromAnotherOriginMessageI18nKey={prefixedKey("follow-project", "anotherProjectUsedInOnboarding")}

      createdOnboardingObject={createdOnboardingProjectFollowership}
      onboardingCreationThanksMessageI18nKey={prefixedKey("follow-project", "thanksForOnboarding")}
      onboardingCreationInfoMessageI18nKey={prefixedKey("follow-project", "notSaved")}
    />

  </>
}

export default withDynamicNamespaces<IProps>(FollowButton, usedNamespaces)